import React, {
    FC, useEffect, useMemo, useState,
} from 'react';

import { Divider, Steps, Tooltip } from 'antd';
import { CloseOutlined, LoadingOutlined, LockOutlined, TeamOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';

import SharingBlock from '@/components/Common/SharingBlock';
import PolicySelect from '@/components/Common/PolicySelect';
import PolicyDescription from '@/components/Common/PolicyDescription';
import RulesLoadingState from '@/components/Common/RulesLoadingState';
import BlockedRecipientsSection, { BlockedRecipient } from '@/components/Common/BlockedRecipientsSection';
import MissingDetailsItem from '@/components/Common/MissingDetails/MissingDetailsItem';
import UsersWithDetailsSection from '@/components/Common/MissingDetails/UsersWithDetailsSection';
import useMissingDetailsState from '@/components/Common/MissingDetails/useMissingDetailsState';
import type { MissingRecipientEntry } from '@/components/Common/MissingDetails/types';
import RevocationPopover from './RevocationPopover';
import { SharedItemType } from '@/components/Common/RecipientRoleSelect';
import { useStores } from '@/components/hooks';
import {
    getPolicyAggregate,
    checkRequiresPhoneVerification,
    checkRequiresPasswordVerification,
    checkRequiresPasswordProtection,
} from '@/components/utils';
import { ApplyRulesResponse, ApplyRulesResult } from '@/api/requests';
import { Policy, SecuredFile } from '@/types/types';
import stepStyles from '@/components/Common/StepNavigation/StepNavigation.module.scss';
import styles from './PermissionDrawer.module.scss';

const { Step } = Steps;
const nameSpace = 'filesTable.permissionDrawer';
const policyStepNameSpace = 'uploadFiles.policyStep';

/* ─── User missing row with revocation ─── */

interface UserMissingRowProps {
    inputs: React.ReactNode;
    emailLabel: React.ReactNode;
    recipientId?: string;
}

const UserMissingRow: FC<UserMissingRowProps> = ({
    inputs, emailLabel, recipientId,
}) => {
    const { sharedUsersStore } = useStores();

    const handleRemove = (): void => {
        if (recipientId) {
            sharedUsersStore.removeRecipient(recipientId);
        }
    };

    return (
        <div className={styles['missing-details-item']}>
            <div className={styles['missing-details-inputs']}>
                {inputs}
                <div className={styles['missing-details-email-row']}>
                    {emailLabel}
                    <CloseOutlined
                        className={styles['missing-details-revoke']}
                        onClick={handleRemove}
                    />
                </div>
            </div>
        </div>
    );
};

/* ─── Group member missing row with revocation ─── */

interface GroupMemberMissingRowProps {
    inputs: React.ReactNode;
    emailLabel: React.ReactNode;
    groupName: string;
    groupId: string;
}

const GroupMemberMissingRow: FC<GroupMemberMissingRowProps> = ({
    inputs, emailLabel, groupName, groupId,
}) => {
    const { t } = useTranslation();
    const { sharedUsersStore } = useStores();
    const [popoverOpen, setPopoverOpen] = useState(false);

    const handleCancel = (): void => setPopoverOpen(false);

    const handleRevoke = (): void => {
        setPopoverOpen(false);
        sharedUsersStore.removeRecipient(groupId);
    };

    return (
        <div className={styles['missing-details-item']}>
            <div className={styles['missing-details-inputs']}>
                {inputs}
                <div className={styles['missing-details-email-group']}>
                    <div className={styles['missing-details-email-row']}>
                        {emailLabel}
                        <RevocationPopover
                            open={popoverOpen}
                            onOpenChange={setPopoverOpen}
                            type="simple"
                            userEmail={groupName}
                            isGroup
                            onCancel={handleCancel}
                            onContinue={handleRevoke}
                        >
                            <CloseOutlined
                                className={styles['missing-details-revoke']}
                                onClick={() => setPopoverOpen(true)}
                            />
                        </RevocationPopover>
                    </div>
                    <span className={styles['missing-details-group-label']}>
                        {t(`${nameSpace}.fromGroup`, 'from')}
                        {' '}
                        <TeamOutlined />
                        {' '}
                        {groupName}
                    </span>
                </div>
            </div>
        </div>
    );
};

/* ─── Review Policy view for Step 2 ─── */

type PerFileVariant = 'choose' | 'assigned' | 'parent';

interface ReviewPolicyViewProps {
    files: SecuredFile[];
    isMultiFile: boolean;
    rulesResults: ApplyRulesResponse;
    selectedPolicy: Policy;
    perFilePolicies?: Map<string, string>;
    onPolicyChange?: (fid: string, policyId: string) => void;
    isPolicyChangeBlockedForFile?: (file: SecuredFile) => boolean;
    folderChildNames?: Map<string, string>;
}

const ReviewPolicyView: FC<ReviewPolicyViewProps> = observer(({
    files,
    isMultiFile,
    rulesResults,
    selectedPolicy,
    perFilePolicies,
    onPolicyChange,
    isPolicyChangeBlockedForFile,
    folderChildNames,
}) => {
    const { t } = useTranslation();
    const { policyStore } = useStores();
    const { policyList } = policyStore;

    const filesMap = useMemo(
        () => new Map(files.map((f) => [f.fid, f])),
        [files],
    );

    const policyRulesMap = useMemo(() => {
        const map = new Map<string, ApplyRulesResult>();
        rulesResults
            .filter((r) => r.action === 'policy' && r.policy_id)
            .forEach((r) => map.set(r.file_id, r));
        return map;
    }, [rulesResults]);

    const getVariant = (f: SecuredFile): PerFileVariant => {
        if (policyRulesMap.has(f.fid)) return 'assigned';
        if (f.is_folder) return 'parent';
        return 'choose';
    };

    const findPolicy = (policyId: string): Policy | null =>
        policyList.find((p) => p.id === policyId) ?? null;

    const resolvePolicyForFile = (f: SecuredFile): Policy | null => {
        const rule = policyRulesMap.get(f.fid);
        if (rule) return findPolicy(rule.policy_id);

        const chosen = perFilePolicies?.get(f.fid);
        if (chosen) return findPolicy(chosen);

        if (f.policy_id) return findPolicy(f.policy_id);

        return selectedPolicy;
    };

    const blockedRecipients: BlockedRecipient[] = useMemo(() => {
        const blockRules = rulesResults.filter((r) => r.action === 'block');
        const allFileIds = new Set(files.map((f) => f.fid));
        const blockedFilesPerRecipient = new Map<string, Set<string>>();

        blockRules.forEach((rule) => {
            const addToMap = (key: string): void => {
                if (!blockedFilesPerRecipient.has(key)) {
                    blockedFilesPerRecipient.set(key, new Set());
                }
                blockedFilesPerRecipient.get(key).add(rule.file_id);
            };
            rule.recipients?.forEach(addToMap);
            rule.groups?.forEach(addToMap);
        });

        const result: BlockedRecipient[] = [];
        blockedFilesPerRecipient.forEach((fileIds, email) => {
            const isAll = allFileIds.size > 0
                && [...allFileIds].every((fid) => fileIds.has(fid));
            result.push({
                email,
                blockedFromAll: isAll,
                fileIds: [...fileIds],
            });
        });

        result.sort((a, b) => {
            if (a.blockedFromAll !== b.blockedFromAll) {
                return a.blockedFromAll ? -1 : 1;
            }
            return a.email.localeCompare(b.email);
        });

        return result;
    }, [rulesResults, files]);

    const hasBlocked = blockedRecipients.length > 0;

    const childRuleGroups = useMemo(() => {
        const folderIds = new Set(files.filter((f) => f.is_folder).map((f) => f.fid));
        const policyRules = rulesResults.filter(
            (r) => r.action === 'policy' && r.policy_id && !folderIds.has(r.file_id),
        );
        if (policyRules.length === 0) return [];
        const groupMap = new Map<string, { rule: ApplyRulesResult; fileIds: string[] }>();
        policyRules.forEach((r) => {
            const existing = groupMap.get(r.policy_id);
            if (existing) {
                if (!existing.fileIds.includes(r.file_id)) existing.fileIds.push(r.file_id);
            } else {
                groupMap.set(r.policy_id, { rule: r, fileIds: [r.file_id] });
            }
        });
        return [...groupMap.values()].map(({ rule, fileIds }) => {
            const policy = findPolicy(rule.policy_id);
            return {
                policyId: rule.policy_id,
                ruleName: rule.rule,
                policy,
                aggregate: policy ? getPolicyAggregate(policy) : null,
                fileIds,
            };
        });
    }, [rulesResults, files, policyList]);

    const { showPhones, showPasswords } = useMemo(() => {
        let phones = false;
        let passwords = false;
        const checkSettings = (policy: Policy | null): void => {
            if (!policy?.settings) return;
            if (checkRequiresPhoneVerification(policy.settings)) {
                phones = true;
            }
            if (
                checkRequiresPasswordVerification(policy.settings)
                || checkRequiresPasswordProtection(policy.settings)
            ) {
                passwords = true;
            }
        };

        files.forEach((f) => {
            const rule = policyRulesMap.get(f.fid);
            if (rule) {
                checkSettings(findPolicy(rule.policy_id));
                return;
            }
            const chosen = perFilePolicies?.get(f.fid);
            if (chosen) {
                checkSettings(findPolicy(chosen));
                return;
            }
            if (f.policy_id) {
                checkSettings(findPolicy(f.policy_id));
                return;
            }
            checkSettings(selectedPolicy);
        });

        childRuleGroups.forEach((group) => checkSettings(group.policy));
        return { showPhones: phones, showPasswords: passwords };
    }, [files, policyRulesMap, policyList, perFilePolicies, selectedPolicy, childRuleGroups]);

    const hasMissingDetails = showPhones || showPasswords;

    const {
        detailsLoaded,
        missingRecipients,
        filledRecipients,
    } = useMissingDetailsState({ showPhones, showPasswords });

    const getFileName = (fid: string): string => {
        const f = filesMap.get(fid);
        if (f) return f.filename;
        return folderChildNames?.get(fid) || fid;
    };

    const getFileIsFolder = (fid: string): boolean => {
        const f = filesMap.get(fid);
        return !!f?.is_folder;
    };

    const getSectionTitle = (variant: PerFileVariant): string => {
        if (variant === 'parent') {
            return t(`${nameSpace}.parentPolicy`);
        }
        if (variant === 'assigned') {
            return t(
                `${policyStepNameSpace}.assignedPolicy`,
                'Assigned Policy',
            );
        }
        return t(`${nameSpace}.choosePolicy`);
    };

    const renderSingleFileView = (): JSX.Element => {
        const f = files[0];
        const variant = getVariant(f);
        const policy = resolvePolicyForFile(f);
        const aggregate = policy ? getPolicyAggregate(policy) : null;
        const rule = policyRulesMap.get(f.fid);
        const isLocked = variant === 'assigned' || !!isPolicyChangeBlockedForFile?.(f);

        return (
            <>
                <div className={styles['section-header']} data-testid="permissionDrawer_reviewPolicySectionHeader">
                    {isLocked && (
                        <LockOutlined className={styles['section-icon']} />
                    )}
                    <span className={styles['section-title']}>
                        {getSectionTitle(variant)}
                    </span>
                </div>
                <div className={styles['review-policy-content']} data-testid="permissionDrawer_reviewPolicySingleContent">
                    <div className={styles['review-policy-layout']}>
                        <div className={styles['review-policy-left']}>
                            <PolicySelect
                                selectedPolicyId={policy?.id}
                                className={styles['review-policy-select']}
                                disabled={isLocked}
                                onChange={!isLocked
                                    ? (pid) => onPolicyChange?.(f.fid, pid)
                                    : undefined}
                            />
                            {variant === 'assigned' && rule && (
                                <div className={styles['review-rule-info']}>
                                    <Trans
                                        t={t}
                                        i18nKey={`${policyStepNameSpace}.assignedDueTo`}
                                        values={{ ruleName: rule.rule }}
                                    >
                                        {'Assigned due to '}
                                        <strong>{'{{ruleName}}'}</strong>
                                    </Trans>
                                </div>
                            )}
                        </div>
                        <div className={styles['review-policy-right']}>
                            {aggregate && (
                                <PolicyDescription
                                    policyAggregate={aggregate}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {f.is_folder && childRuleGroups.length > 0 && (
                    <>
                        <div className={styles['section-header']} data-testid="permissionDrawer_reviewFolderChildPoliciesHeader">
                            <LockOutlined className={styles['section-icon']} />
                            <span className={styles['section-title']}>
                                {t(
                                    `${policyStepNameSpace}.assignedPolicy`,
                                    'Assigned Policy',
                                )}
                            </span>
                        </div>
                        {childRuleGroups.map((group) => (
                            <div
                                key={group.policyId}
                                className={styles['review-policy-content']}
                                data-testid={`permissionDrawer_reviewFolderChildPolicy_${group.policyId}`}
                            >
                                <div className={styles['review-policy-layout']}>
                                    <div className={styles['review-policy-left']}>
                                        <PolicySelect
                                            selectedPolicyId={group.policy?.id}
                                            className={styles['review-policy-select']}
                                            disabled
                                        />
                                        <div className={styles['review-rule-info']}>
                                            <Trans
                                                t={t}
                                                i18nKey={`${policyStepNameSpace}.assignedDueTo`}
                                                values={{ ruleName: group.ruleName }}
                                            >
                                                {'Assigned due to '}
                                                <strong>{'{{ruleName}}'}</strong>
                                            </Trans>
                                        </div>
                                        <div className={styles['assigned-items-label']}>
                                            {t(
                                                `${nameSpace}.assignedTo`,
                                                'Assigned to',
                                            )}
                                            {' '}
                                            <Tooltip
                                                placement="bottom"
                                                color="#091F56"
                                                title={(
                                                    <div>
                                                        {group.fileIds.map((fid) => (
                                                            <div key={fid}>
                                                                {getFileName(fid)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            >
                                                <span className={styles['assigned-items-link']}>
                                                    {t(
                                                        `${nameSpace}.itemCount`,
                                                        {
                                                            count: group.fileIds.length,
                                                            defaultValue: '{{count}} item',
                                                            defaultValue_plural: '{{count}} items',
                                                        },
                                                    )}
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </div>
                                    <div className={styles['review-policy-right']}>
                                        {group.aggregate && (
                                            <PolicyDescription
                                                policyAggregate={group.aggregate}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </>
                )}
            </>
        );
    };

    const renderFileRow = (f: SecuredFile): JSX.Element => {
        const policy = resolvePolicyForFile(f);
        const aggregate = policy ? getPolicyAggregate(policy) : null;
        const variant = getVariant(f);
        const isLocked = variant === 'assigned' || !!isPolicyChangeBlockedForFile?.(f);
        const rule = policyRulesMap.get(f.fid);

        return (
            <div key={f.fid} className={styles['per-file-row']} data-testid={`permissionDrawer_reviewPerFileRow_${f.fid}`}>
                <div className={styles['review-policy-layout']}>
                    <div className={styles['review-policy-left']}>
                        <PolicySelect
                            selectedPolicyId={policy?.id}
                            className={styles['review-policy-select']}
                            disabled={isLocked}
                            onChange={!isLocked
                                ? (pid) => onPolicyChange?.(f.fid, pid)
                                : undefined}
                        />
                        {isLocked && rule && (
                            <div className={styles['review-rule-info']}>
                                <Trans
                                    t={t}
                                    i18nKey={`${policyStepNameSpace}.assignedDueTo`}
                                    values={{ ruleName: rule.rule }}
                                >
                                    {'Assigned due to '}
                                    <strong>{'{{ruleName}}'}</strong>
                                </Trans>
                            </div>
                        )}
                        <div className={styles['per-file-name']}>
                            {f.filename}
                        </div>
                    </div>
                    <div className={styles['review-policy-right']}>
                        {aggregate && (
                            <PolicyDescription
                                policyAggregate={aggregate}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const variantGroups = useMemo(() => {
        const order: PerFileVariant[] = ['parent', 'choose'];
        const map = new Map<PerFileVariant, SecuredFile[]>();
        files.forEach((f) => {
            if (policyRulesMap.has(f.fid)) return;
            const v: PerFileVariant = f.is_folder ? 'parent' : 'choose';
            if (!map.has(v)) map.set(v, []);
            map.get(v).push(f);
        });
        return order
            .filter((v) => map.has(v))
            .map((v) => ({ variant: v, items: map.get(v) }));
    }, [files, policyRulesMap]);

    const renderMultiFileView = (): JSX.Element => (
        <>
            {variantGroups.map(({ variant, items }) => (
                <div
                    key={variant}
                    className={styles['per-file-card']}
                    data-testid={`permissionDrawer_reviewPerFileCard_${variant}`}
                >
                    <div className={styles['per-file-card-header']} data-testid={`permissionDrawer_reviewPerFileCardHeader_${variant}`}>
                        {variant === 'assigned' && (
                            <LockOutlined
                                className={styles['section-icon']}
                            />
                        )}
                        <span className={styles['section-title']}>
                            {getSectionTitle(variant)}
                        </span>
                    </div>
                    <div className={styles['per-file-card-content']}>
                        {items.map(renderFileRow)}
                    </div>
                </div>
            ))}

            {childRuleGroups.length > 0 && (
                <div className={styles['per-file-card']}>
                    <div className={styles['per-file-card-header']}>
                        <LockOutlined className={styles['section-icon']} />
                        <span className={styles['section-title']}>
                            {t(
                                `${policyStepNameSpace}.assignedPolicy`,
                                'Assigned Policy',
                            )}
                        </span>
                    </div>
                    <div className={styles['per-file-card-content']}>
                        {childRuleGroups.map((group) => (
                            <div key={group.policyId} className={styles['per-file-row']}>
                                <div className={styles['review-policy-layout']}>
                                    <div className={styles['review-policy-left']}>
                                        <PolicySelect
                                            selectedPolicyId={group.policy?.id}
                                            className={styles['review-policy-select']}
                                            disabled
                                        />
                                        <div className={styles['review-rule-info']}>
                                            <Trans
                                                t={t}
                                                i18nKey={`${policyStepNameSpace}.assignedDueTo`}
                                                values={{ ruleName: group.ruleName }}
                                            >
                                                {'Assigned due to '}
                                                <strong>{'{{ruleName}}'}</strong>
                                            </Trans>
                                        </div>
                                        <div className={styles['assigned-items-label']}>
                                            {t(`${nameSpace}.assignedTo`, 'Assigned to')}
                                            {' '}
                                            <Tooltip
                                                placement="bottom"
                                                color="#091F56"
                                                title={(
                                                    <div>
                                                        {group.fileIds.map((fid) => (
                                                            <div key={fid}>{getFileName(fid)}</div>
                                                        ))}
                                                    </div>
                                                )}
                                            >
                                                <span className={styles['assigned-items-link']}>
                                                    {t(`${nameSpace}.itemCount`, {
                                                        count: group.fileIds.length,
                                                        defaultValue: '{{count}} item',
                                                        defaultValue_plural: '{{count}} items',
                                                    })}
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </div>
                                    <div className={styles['review-policy-right']}>
                                        {group.aggregate && (
                                            <PolicyDescription
                                                policyAggregate={group.aggregate}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div className={styles['review-policy-view']} data-testid="permissionDrawer_reviewPolicyView">
            {isMultiFile
                ? renderMultiFileView()
                : renderSingleFileView()}

            {hasMissingDetails && !detailsLoaded && (
                <div className={styles['details-loading']} data-testid="permissionDrawer_detailsLoading">
                    <LoadingOutlined spin />
                </div>
            )}

            {missingRecipients.length > 0 && (
                <div className={styles['missing-details-section']} data-testid="permissionDrawer_reviewMissingDetailsSection">
                    <div className={styles['section-header']}>
                        <span className={styles['section-title']}>
                            {t(
                                `${policyStepNameSpace}.missingDetails`,
                                'Missing details',
                            )}
                        </span>
                    </div>
                    <div className={styles['missing-details-list']} data-testid="permissionDrawer_reviewMissingDetailsList">
                        {missingRecipients.map((entry) => (
                            <MissingDetailsItem
                                key={`${entry.email}-${entry.groupName || ''}`}
                                email={entry.email}
                                showPhones={showPhones}
                                showPasswords={showPasswords}
                                className={
                                    styles['missing-details-item']
                                }
                                inputsClassName={
                                    styles['missing-details-inputs']
                                }
                                emailClassName={
                                    styles['missing-details-email']
                                }
                                passwordBlockClassName={
                                    styles[
                                        'missing-details-password-block'
                                    ]
                                }
                                renderLayout={entry.groupName
                                    ? (inputs, emailLabel) => (
                                        <GroupMemberMissingRow
                                            inputs={inputs}
                                            emailLabel={emailLabel}
                                            groupName={entry.groupName}
                                            groupId={entry.groupId}
                                        />
                                    )
                                    : (inputs, emailLabel) => (
                                        <UserMissingRow
                                            inputs={inputs}
                                            emailLabel={emailLabel}
                                            recipientId={entry.recipientId}
                                        />
                                    )}
                            />
                        ))}
                    </div>
                </div>
            )}

            {hasBlocked && (
                <div data-testid="permissionDrawer_reviewBlockedRecipients">
                    <BlockedRecipientsSection
                        blockedRecipients={blockedRecipients}
                        getFileName={getFileName}
                        getFileIsFolder={getFileIsFolder}
                    />
                </div>
            )}

            <UsersWithDetailsSection
                filledRecipients={filledRecipients}
                showPhones={showPhones}
                showPasswords={showPasswords}
                titleNameSpace={policyStepNameSpace}
            />
        </div>
    );
});

/* ─── Add Recipients view (main export) ─── */

interface AddRecipientsViewProps {
    files: SecuredFile[];
    isMultiFile: boolean;
    showPhones: boolean;
    showPasswords: boolean;
    currentStep: number;
    isLoadingRules: boolean;
    rulesResults: ApplyRulesResponse;
    selectedPolicy: Policy;
    perFilePolicies?: Map<string, string>;
    onPerFilePolicyChange?: (fid: string, policyId: string) => void;
    isPolicyChangeBlockedForFile?: (file: SecuredFile) => boolean;
    folderChildNames?: Map<string, string>;
}

const AddRecipientsView: FC<AddRecipientsViewProps> = observer(({
    files,
    isMultiFile,
    showPhones,
    showPasswords,
    currentStep,
    isLoadingRules,
    rulesResults,
    selectedPolicy,
    perFilePolicies,
    onPerFilePolicyChange,
    isPolicyChangeBlockedForFile,
    folderChildNames,
}) => {
    const { t } = useTranslation();
    const { sharedUsersStore } = useStores();

    const file = files.length === 1 ? files[0] : null;
    const sharedItemType: SharedItemType = file?.is_folder || file?.is_workspace ? 'folder' : 'file';

    useEffect(() => {
        if (currentStep === 0) {
            sharedUsersStore.fetchEmailsList();
        }
    }, []);

    return (
        <div className={styles['add-recipients-view']} data-testid="permissionDrawer_addRecipientsView">
            <div className={stepStyles['step-navigation']} data-testid="permissionDrawer_addRecipientsSteps">
                <Steps
                    current={currentStep}
                    className={stepStyles.steps}
                    labelPlacement="horizontal"
                >
                    <Step
                        title={t(`${nameSpace}.addNewRecipients`)}
                        className={stepStyles['step-item']}
                    />
                    <Step
                        title={t(
                            'shareDrawer.steps.chooseOrReviewPolicy',
                            'Choose or Review Policy',
                        )}
                        className={stepStyles['step-item']}
                    />
                </Steps>
            </div>
            <Divider style={{ margin: 0 }} />
            <div className={styles['add-recipients-content']} data-testid="permissionDrawer_addRecipientsContent">
                {currentStep === 0 && (
                    <div className={styles['step1-content']} data-testid="permissionDrawer_addRecipientsStep1">
                        <SharingBlock
                            showPhones={false}
                            showPasswords={false}
                            sharedItemType={sharedItemType}
                            hasSharedUsers
                            inlineRoleSelect
                        />
                    </div>
                )}
                {currentStep === 1 && (
                    isLoadingRules
                        ? (
                            <div data-testid="permissionDrawer_addRecipientsRulesLoading">
                                <RulesLoadingState />
                            </div>
                        )
                        : (
                            <div data-testid="permissionDrawer_addRecipientsStep2">
                                <ReviewPolicyView
                                    files={files}
                                    isMultiFile={isMultiFile}
                                    rulesResults={rulesResults}
                                    selectedPolicy={selectedPolicy}
                                    perFilePolicies={perFilePolicies}
                                    onPolicyChange={onPerFilePolicyChange}
                                    isPolicyChangeBlockedForFile={isPolicyChangeBlockedForFile}
                                    folderChildNames={folderChildNames}
                                />
                            </div>
                        )
                )}
            </div>
        </div>
    );
});

export default AddRecipientsView;
